import React from 'react';

import { useFloating, offset, useMergeRefs, useDismiss, FloatingPortal, shift } from '@floating-ui/react';
import { DraftDecorator, EditorState, KeyBindingUtil, Modifier, RichUtils, SelectionState } from 'draft-js';
import { complement, concat, when, test } from 'ramda';

import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';
import LinkIcon from 'components/shared/Icon/icons/Link';
import { TabSelector } from 'components/shared/TabSelector';
import { inlineStylesMap } from 'components/shared/Wysiwyg/customStyles';
import { useWysiwygContext } from 'components/shared/Wysiwyg/Wysiwyg.contex';
import { MOBILE_OPERATING_SYSTEMS } from 'constants/mobileApplication';
import { getMobileOperatingSystem } from 'utils/mobileApplication';
import { isDefined } from 'utils/ramda';

import { BaseItemButton } from '../Base';
import { LINK_TABS } from './Link.constants';
import s from './Link.module.css';
import * as t from './Link.types';
import { LinkPortalController } from './useLinkPortal';
import { WysiwygLinkType } from './useLinkPortal/useLinkPortal.types';

export const Link = () => {
  const { editor } = useWysiwygContext();
  const selectionState = React.useRef<SelectionState | null>(null);

  const openPortalNearSelection = () => {
    selectionState.current = editor.editorState.getSelection();

    if (editor.linkPortalController.portalData || !selectionState.current) return;

    if (!selectionState.current.isCollapsed()) {
      editor.setIsEditorActive(false);
      editor.setEditorState(inlineStylesMap.selected.toggle(editor.editorState, 'yes'));

      const range = window.getSelection()?.getRangeAt(0);
      if (range) {
        const rect = range.getBoundingClientRect();
        editor.linkPortalController.setPortalData({ mode: 'create', rect, onClose: close, onSubmit: onAddLink });
      }
    }
  };

  const close = () => {
    if (selectionState.current) {
      editor.setEditorState(
        EditorState.forceSelection(inlineStylesMap.selected.remove(editor.editorState, 'yes'), selectionState.current),
      );
    }
    editor.setIsEditorActive(true);
    editor.linkPortalController.setPortalData(null);
  };

  const onAddLink = (link: string, type: WysiwygLinkType) => {
    if (!selectionState.current) {
      return;
    }

    const contentState = editor.editorState.getCurrentContent();

    const contentWithoutStyles = Modifier.removeInlineStyle(
      contentState,
      selectionState.current,
      'CUSTOM_SELECTED_yes',
    );

    if (!link) {
      const newEditorState = RichUtils.toggleLink(
        EditorState.push(editor.editorState, contentWithoutStyles, 'change-inline-style'),
        selectionState.current,
        null,
      );
      editor.handleChange(newEditorState, true);
      return;
    }

    const contentWithEntity = contentWithoutStyles.createEntity(type, 'MUTABLE', { url: link });
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    const newContentState = Modifier.applyEntity(contentWithEntity, selectionState.current, entityKey);

    const newEditorState = EditorState.push(editor.editorState, newContentState, 'apply-entity');

    editor.handleChange(newEditorState, true);
    editor.setIsEditorActive(true);
    editor.linkPortalController.setPortalData(null);
  };

  return (
    <BaseItemButton onMouseDown={openPortalNearSelection}>
      <Icon component={LinkIcon} />
    </BaseItemButton>
  );
};

export const LinkDecorator = (isReadOnly: boolean, props: t.LinkDecoratorProps) => {
  const { editor } = useWysiwygContext();
  const ref = React.useRef<HTMLAnchorElement>(null);
  const data = props.contentState.getEntity(props.entityKey).getData();
  const type = props.contentState.getEntity(props.entityKey).getType() as WysiwygLinkType;

  const url = (() => {
    if (type === WysiwygLinkType.email) return when(complement(test(/^mailto:/)), concat('mailto:'))(data.url);
    if (type === WysiwygLinkType.tel) return when(complement(test(/^tel:/)), concat('tel:'))(data.url);

    return when(complement(test(/^https?:\/\//)), concat('https://'))(data.url);
  })();

  const openPortal = (e: React.MouseEvent) => {
    if (isReadOnly) return;

    e.stopPropagation();
    e.preventDefault();

    if (!ref.current) {
      return;
    }

    editor.setIsEditorActive(false);
    const rect = ref.current.getBoundingClientRect();
    editor.linkPortalController.setPortalData({
      mode: 'edit',
      initialUrl: url,
      type,
      rect,
      onClose: close,
      onSubmit: onChangeLink,
      onDelete: onRemoveLink,
    });
  };

  const close = () => {
    editor.setIsEditorActive(true);
    editor.linkPortalController.setPortalData(null);
  };

  const onChangeLink = (link: string) => {
    const contentState = editor.editorState.getCurrentContent();
    const updatedContentState = contentState.replaceEntityData(props.entityKey, { url: link });
    const newEditorState = EditorState.push(editor.editorState, updatedContentState, 'apply-entity');
    editor.handleChange(newEditorState);
    const newChangeState = editor.getChangeStateFromEditorState(newEditorState);
    editor.emitChange(newChangeState);
    close();
  };

  const onRemoveLink = () => {
    const contentState = editor.editorState.getCurrentContent();

    contentState.getBlockMap().forEach((block) => {
      block!.findEntityRanges(
        (character) => character.getEntity() === props.entityKey,
        (start, end) => {
          const selectionState = SelectionState.createEmpty(block!.getKey()).merge({
            anchorOffset: start,
            focusOffset: end,
          });

          const contentStateWithoutEntity = Modifier.applyEntity(contentState, selectionState, null);

          const newEditorState = EditorState.push(editor.editorState, contentStateWithoutEntity, 'apply-entity');
          editor.handleChange(newEditorState);
        },
      );
    });

    close();
  };

  return (
    <a
      ref={ref}
      href={url}
      className={s.decoratorLink}
      title={`Link: ${url}`}
      target="_blank"
      rel="noopener noreferrer"
      data-external-link=""
      onClick={openPortal}
    >
      {props.children}
    </a>
  );
};

export const findLinkEntities: DraftDecorator['strategy'] = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      (Object.values(WysiwygLinkType) as string[]).includes(contentState.getEntity(entityKey).getType())
    );
  }, callback);
};

export const linkKeyBindingHandler = (e: React.KeyboardEvent) => {
  if (KeyBindingUtil.hasCommandModifier(e) && e.key === 'k') {
    return 'add-link';
  }
  return null;
};

export const linkHandleKeyCommand = (
  command: string,
  editorState: EditorState,
  setEditorState: (editorState: EditorState) => void,
  setIsEditorActive: (isEditorActive: boolean) => void,
  handleChange: (editorState: EditorState, forceSelection?: boolean) => void,
  linkPortalController: LinkPortalController,
): 'handled' | 'not-handled' => {
  let _selectionState: null | SelectionState = null;

  const openPortalNearSelection = () => {
    _selectionState = editorState.getSelection();

    if (linkPortalController.portalData) return;

    if (!_selectionState.isCollapsed()) {
      setIsEditorActive(false);
      setEditorState(inlineStylesMap.selected.toggle(editorState, 'yes'));

      const range = window.getSelection()?.getRangeAt(0);
      if (range) {
        const rect = range.getBoundingClientRect();
        linkPortalController.setPortalData({ mode: 'create', rect, onClose: close, onSubmit: onAddLink });
      }
    }
  };

  const onAddLink = (link: string, type: WysiwygLinkType) => {
    if (!_selectionState) {
      return;
    }

    const contentState = editorState.getCurrentContent();
    const contentWithoutStyles = Modifier.removeInlineStyle(contentState, _selectionState, 'CUSTOM_SELECTED_yes');

    if (!link) {
      const newEditorState = RichUtils.toggleLink(
        EditorState.push(editorState, contentWithoutStyles, 'change-inline-style'),
        _selectionState,
        null,
      );
      handleChange(newEditorState, true);
      return;
    }

    const contentWithEntity = contentWithoutStyles.createEntity(type, 'MUTABLE', { url: link });
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    const newContentState = Modifier.applyEntity(contentWithEntity, _selectionState, entityKey);

    const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');

    handleChange(newEditorState, true);
    setIsEditorActive(true);
    linkPortalController.setPortalData(null);
  };

  const close = () => {
    if (_selectionState) {
      setEditorState(EditorState.forceSelection(inlineStylesMap.selected.remove(editorState, 'yes'), _selectionState));
    }
    setIsEditorActive(true);
    linkPortalController.setPortalData(null);
  };

  if (command === 'add-link') {
    openPortalNearSelection();
    return 'handled';
  }

  return 'not-handled';
};

export const LinkPortal = () => {
  const { editor } = useWysiwygContext();
  const portalData = editor.linkPortalController.portalData;
  const isEdit = portalData?.mode === 'edit';
  const isOpen = Boolean(portalData);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [linkValue, setLinkValue] = React.useState<string>('');
  const [linkType, setLinkType] = React.useState<WysiwygLinkType>(WysiwygLinkType.url);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (state) => {
      if (!state && portalData) {
        setTimeout(portalData.onClose);
        setLinkValue('');
      }
    },
    middleware: [offset(8), shift()],
    placement: 'bottom-start',
  });
  const mergedRef = useMergeRefs([refs.setFloating, editor.refs.portalRef]);
  useDismiss(context, { enabled: isOpen });

  const OS = getMobileOperatingSystem();
  const isIOS = OS === MOBILE_OPERATING_SYSTEMS.ios;

  React.useEffect(() => {
    if (!portalData) return;

    const mobileNavBlockRect = document.getElementById('stuff')?.getBoundingClientRect();
    const portalRenderRect = (() => {
      if (isIOS && isDefined(mobileNavBlockRect)) return mobileNavBlockRect;
      return portalData.rect;
    })();

    if (portalRenderRect) {
      refs.setReference({
        getBoundingClientRect: () => portalRenderRect,
      });
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }

    if (portalData.mode === 'create') {
      setLinkValue('');
      setLinkType(WysiwygLinkType.url);
    } else {
      setLinkValue(portalData.initialUrl);
      setLinkType(portalData.type);
    }
  }, [isOpen]);

  const submit = () => {
    if (!portalData) return;

    if (isEdit && !linkValue) {
      portalData.onDelete();
      return;
    } else if (portalData.mode === 'create' && !linkValue) {
      portalData.onClose();
      return;
    }

    portalData.onSubmit(linkValue, linkType);
  };

  if (!isOpen) return null;

  const placeholder = (() => {
    if (linkType === WysiwygLinkType.email) return 'safari@example.com';
    if (linkType === WysiwygLinkType.tel) return '+11234567890';

    return 'https://www.example.com/';
  })();

  const linkTypeIcon = (() => {
    const icon = LINK_TABS.find(({ type }) => type === linkType)?.icon;
    if (!icon) return null;
    return <Icon component={icon} width="18px" height="18px" stroke="#3f3c43" className={s.portalIcon} fill="none" />;
  })();

  const handleValue = (value: string) => {
    if (linkType === WysiwygLinkType.email) return value.replace(/^mailto:/, '');
    if (linkType === WysiwygLinkType.tel) return value.replace(/^tel:/, '');

    return value;
  };

  return (
    <FloatingPortal>
      <div ref={mergedRef} style={floatingStyles} className={s.portalWrapper} onClick={(e) => e.stopPropagation()}>
        {!isEdit && (
          <TabSelector
            tabs={LINK_TABS}
            onTabChange={(index) => setLinkType(LINK_TABS[index].type)}
            tabsClassName={s.tabsSelector}
          />
        )}

        <div className={s.portalContentContainer}>
          <div className={s.portalLeftPart}>
            {linkTypeIcon}

            <input
              value={handleValue(linkValue)}
              autoFocus
              placeholder={placeholder}
              className={s.portalInput}
              ref={inputRef}
              onKeyDown={(e) => {
                e.stopPropagation();

                if (e.key === 'Escape') {
                  e.preventDefault();
                  portalData!.onClose();
                  return;
                }

                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              onChange={(e) => {
                e.stopPropagation();
                setLinkValue(e.target.value);
              }}
            />
          </div>

          <Button
            type="default"
            shape="ellipse"
            className={s.portalBtn}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              submit();
            }}
            size="small"
          >
            Apply
          </Button>
        </div>
      </div>
    </FloatingPortal>
  );
};
